export type Granularity = 'month' | 'quarter' | 'year';

export interface PeriodBucket {
  key: string;
  label: string;
  start: string;
  end: string;
}

const QUARTERS: { key: string; label: string; months: [string, string] }[] = [
  { key: 'Q1', label: '1er trim. (Avr–Juin)', months: ['04', '06'] },
  { key: 'Q2', label: '2ème trim. (Juil–Sep)', months: ['07', '09'] },
  { key: 'Q3', label: '3ème trim. (Oct–Déc)', months: ['10', '12'] },
  { key: 'Q4', label: '4ème trim. (Jan–Mar)', months: ['01', '03'] },
];

const MONTHS: { key: string; label: string }[] = [
  { key: '04', label: 'Avril' }, { key: '05', label: 'Mai' },
  { key: '06', label: 'Juin' }, { key: '07', label: 'Juillet' },
  { key: '08', label: 'Août' }, { key: '09', label: 'Septembre' },
  { key: '10', label: 'Octobre' }, { key: '11', label: 'Novembre' },
  { key: '12', label: 'Décembre' }, { key: '01', label: 'Janvier' },
  { key: '02', label: 'Février' }, { key: '03', label: 'Mars' },
];

function lastDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 0).getDate();
}

export function getPeriodBuckets(fiscalYear: number, granularity: Granularity): PeriodBucket[] {
  if (granularity === 'year') {
    return [{
      key: 'YEAR',
      label: `Année ${fiscalYear} – ${fiscalYear + 1}`,
      start: `${fiscalYear}-04-01`,
      end: `${fiscalYear + 1}-03-31`,
    }];
  }

  if (granularity === 'quarter') {
    return QUARTERS.map(q => {
      const [startMonth, endMonth] = q.months;
      const year = q.key === 'Q4' ? fiscalYear + 1 : fiscalYear;
      const endDay = lastDayOfMonth(year, parseInt(endMonth, 10));
      return {
        key: q.key,
        label: q.label,
        start: `${year}-${startMonth}-01`,
        end: `${year}-${endMonth}-${String(endDay).padStart(2, '0')}`,
      };
    });
  }

  return MONTHS.map(m => {
    const year = parseInt(m.key, 10) <= 3 ? fiscalYear + 1 : fiscalYear;
    const endDay = lastDayOfMonth(year, parseInt(m.key, 10));
    return {
      key: m.key,
      label: m.label,
      start: `${year}-${m.key}-01`,
      end: `${year}-${m.key}-${String(endDay).padStart(2, '0')}`,
    };
  });
}

export function getPeriodKeyForDate(dateStr: string | null | undefined, buckets: PeriodBucket[]): string | null {
  if (!dateStr) return null;
  const found = buckets.find(b => dateStr >= b.start && dateStr <= b.end);
  return found ? found.key : null;
}

export function diffDays(later: string | null | undefined, earlier: string | null | undefined): number | null {
  if (!later || !earlier) return null;
  const a = new Date(later).getTime();
  const b = new Date(earlier).getTime();
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  return Math.round((a - b) / (1000 * 60 * 60 * 24));
}

const PRICE_BRACKETS: { max: number | null; label: string }[] = [
  { max: 100000, label: '< 100k€' },
  { max: 150000, label: '100-150k€' },
  { max: 200000, label: '150-200k€' },
  { max: 250000, label: '200-250k€' },
  { max: 300000, label: '250-300k€' },
  { max: 400000, label: '300-400k€' },
  { max: 500000, label: '400-500k€' },
  { max: null, label: '500k€+' },
];

export const PRICE_BRACKET_LABELS = PRICE_BRACKETS.map(b => b.label);

export function getPriceBracket(price: number | null | undefined): string | null {
  if (price === null || price === undefined || Number.isNaN(price)) return null;
  const bracket = PRICE_BRACKETS.find(b => b.max === null || price < b.max);
  return bracket ? bracket.label : null;
}

export function average(values: number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((sum, v) => sum + v, 0) / values.length;
}

export function buildPeriodColumnAverages<T>(
  items: T[],
  periods: PeriodBucket[],
  columnKeys: string[],
  getPeriodKey: (item: T) => string | null,
  getColumnKey: (item: T) => string | null,
  getValue: (item: T) => number | null
): Map<string, Map<string, number | null>> {
  const raw = new Map<string, Map<string, number[]>>();
  periods.forEach(p => {
    const row = new Map<string, number[]>();
    columnKeys.forEach(key => row.set(key, []));
    raw.set(p.key, row);
  });

  items.forEach(item => {
    const periodKey = getPeriodKey(item);
    if (!periodKey || !raw.has(periodKey)) return;
    const columnKey = getColumnKey(item);
    if (!columnKey) return;
    const value = getValue(item);
    if (value === null) return;
    raw.get(periodKey)?.get(columnKey)?.push(value);
  });

  const result = new Map<string, Map<string, number | null>>();
  raw.forEach((row, periodKey) => {
    const out = new Map<string, number | null>();
    row.forEach((values, columnKey) => out.set(columnKey, average(values)));
    result.set(periodKey, out);
  });
  return result;
}

export function buildPeriodCategoryCounts<T>(
  items: T[],
  periods: PeriodBucket[],
  categoryKeys: string[],
  getPeriodKey: (item: T) => string | null,
  getCategoryKey: (item: T) => string | null
): Map<string, Map<string, number>> {
  const result = new Map<string, Map<string, number>>();
  periods.forEach(p => {
    const row = new Map<string, number>();
    categoryKeys.forEach(key => row.set(key, 0));
    result.set(p.key, row);
  });

  items.forEach(item => {
    const periodKey = getPeriodKey(item);
    if (!periodKey || !result.has(periodKey)) return;
    const categoryKey = getCategoryKey(item);
    if (!categoryKey) return;
    const row = result.get(periodKey)!;
    row.set(categoryKey, (row.get(categoryKey) ?? 0) + 1);
  });

  return result;
}

/**
 * Comme buildPeriodCategoryCounts, mais chaque catégorie a son propre prédicat :
 * un même item peut être compté dans plusieurs catégories à la fois (non-exclusif).
 */
export function buildPeriodMultiCategoryCounts<T>(
  items: T[],
  periods: PeriodBucket[],
  categories: { key: string; predicate: (item: T) => boolean }[],
  getPeriodKey: (item: T) => string | null
): Map<string, Map<string, number | null>> {
  const result = new Map<string, Map<string, number | null>>();
  periods.forEach(p => {
    const row = new Map<string, number | null>();
    categories.forEach(c => row.set(c.key, 0));
    result.set(p.key, row);
  });

  items.forEach(item => {
    const periodKey = getPeriodKey(item);
    if (!periodKey || !result.has(periodKey)) return;
    const row = result.get(periodKey)!;
    categories.forEach(c => {
      if (c.predicate(item)) row.set(c.key, (row.get(c.key) ?? 0) as number + 1);
    });
  });

  return result;
}
